import React from 'react';
import { Box, Typography, Fab, Tooltip } from '@mui/material';
import { Add, Apps as AppsIcon } from '@mui/icons-material';
import { LOGIN_ACCENT } from '../../auth/loginDarkTheme';
import { mobileBottomNavSx } from './mobileAppTheme';

export default function MobileBottomNav({
  leftTabs = [],
  rightTabs = [],
  activePath,
  onNavigate,
  onTabClick,
  onFabClick,
  onMoreClick,
  showMoreTab = true,
  centerFabContent = null,
  centerFabAriaLabel = 'Quick action',
  className,
  style,
}) {
  const handleTabPress = (tab) => {
    if (tab?.disabled) return;
    if (typeof tab.onClick === 'function') {
      tab.onClick();
      return;
    }
    if (onTabClick) {
      onTabClick(tab);
      return;
    }
    if (tab.url) {
      onNavigate?.(tab.url);
    }
  };

  const renderTab = (tab) => {
    const active =
      !tab.disabled
      && (tab.isActive
        ?? (tab.match ? tab.match(activePath) : activePath === tab.url));
    const Icon = tab.icon;
    const disabled = Boolean(tab.disabled);
    const content = (
      <Box
        onClick={() => handleTabPress(tab)}
        aria-disabled={disabled || undefined}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.35,
          cursor: disabled ? 'not-allowed' : 'pointer',
          color: active ? LOGIN_ACCENT : 'rgba(148,163,184,0.9)',
          opacity: disabled ? 0.42 : 1,
          filter: disabled ? 'grayscale(0.45)' : 'none',
          minWidth: 0,
        }}
      >
        <Icon sx={{ fontSize: 24 }} />
        <Typography sx={{ fontSize: '0.72rem', fontWeight: active ? 700 : 500 }}>{tab.label}</Typography>
      </Box>
    );
    if (!disabled || !tab.disabledTooltip) {
      return <React.Fragment key={tab.id}>{content}</React.Fragment>;
    }
    return (
      <Tooltip key={tab.id} title={tab.disabledTooltip} arrow placement="top">
        <Box component="span" sx={{ display: 'flex', justifyContent: 'center', minWidth: 0 }}>
          {content}
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box component="nav" className={className} style={style} sx={mobileBottomNavSx}>
      <Box
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 60px 1fr 1fr',
          alignItems: 'end',
          gap: 0.35,
        }}
      >
        {leftTabs[0] ? renderTab(leftTabs[0]) : <Box />}
        {leftTabs[1] ? renderTab(leftTabs[1]) : <Box />}
        <Box sx={{ display: 'flex', justifyContent: 'center', position: 'relative', top: -14 }}>
          <Fab
            aria-label={centerFabAriaLabel}
            onClick={onFabClick}
            sx={{
              width: 54,
              height: 54,
              background: `linear-gradient(135deg, ${LOGIN_ACCENT} 0%, #14b8a6 100%)`,
              color: '#04120e',
              boxShadow: `0 8px 24px rgba(76,225,182,0.35)`,
              overflow: 'hidden',
            }}
          >
            {centerFabContent || <Add sx={{ fontSize: 28 }} />}
          </Fab>
        </Box>
        {rightTabs[0] ? renderTab(rightTabs[0]) : <Box />}
        {rightTabs[1] ? (
          renderTab(rightTabs[1])
        ) : showMoreTab ? (
          <Box
            onClick={onMoreClick}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.35,
              cursor: 'pointer',
              color: 'rgba(148,163,184,0.9)',
            }}
          >
            <AppsIcon sx={{ fontSize: 24 }} />
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 500 }}>More</Typography>
          </Box>
        ) : (
          <Box />
        )}
      </Box>
    </Box>
  );
}
